import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  Bookmark01Icon,
  ThumbsUpIcon,
  Message01Icon,
  Comment01Icon,
  SentIcon,
  Delete02Icon,
} from '@hugeicons/core-free-icons';
import { api, apiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

/**
 * Engagement del Knowledge Hub, estilo redes sociales.
 *
 * Composición (las páginas colocan las piezas donde corresponde):
 *   <KBEngagementProvider articleId=... initial=...>
 *     <KBEngagementBar />        // barra sticky bajo el header (Guardar/Like/Comentar)
 *     ...contenido de la página...
 *     <KBComments />             // hilo de comentarios al final
 *   </KBEngagementProvider>
 *
 * El compositor de comentarios se abre en un modal desde la barra o desde el hilo.
 */

const COMMENTS_ANCHOR_ID = 'kb-comentarios';

const EngagementCtx = createContext(null);
const useEngagement = () => useContext(EngagementCtx);

export function KBEngagementProvider({ articleId, initial, children }) {
  const { user } = useAuth();
  const [state, setState] = useState({
    likes: initial?.likes ?? 0,
    favorites: initial?.favorites ?? 0,
    comments: initial?.comments ?? 0,
    liked_by_me: initial?.liked_by_me ?? false,
    favorited_by_me: initial?.favorited_by_me ?? false,
  });
  const [comments, setComments] = useState(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    if (!articleId) return;
    let cancelled = false;
    if (!initial) {
      api
        .get(`/kb/articles/${articleId}/engagement`)
        .then(({ data }) => !cancelled && setState((s) => ({ ...s, ...data })))
        .catch(() => {});
    }
    api
      .get(`/kb/articles/${articleId}/comments`)
      .then(({ data }) => !cancelled && setComments(data))
      .catch(() => !cancelled && setComments([]));
    return () => {
      cancelled = true;
    };
  }, [articleId, initial]);

  const toggleFavorite = async () => {
    const next = !state.favorited_by_me;
    setState((s) => ({ ...s, favorited_by_me: next, favorites: s.favorites + (next ? 1 : -1) }));
    try {
      const { data } = await api.post(`/kb/articles/${articleId}/favorite`, { favorite: next });
      setState((s) => ({ ...s, favorited_by_me: data.favorited_by_me, favorites: data.favorites }));
    } catch (e) {
      setState((s) => ({ ...s, favorited_by_me: !next, favorites: s.favorites + (next ? -1 : 1) }));
      toast.error(apiError(e));
    }
  };

  const toggleLike = async () => {
    const next = !state.liked_by_me;
    setState((s) => ({ ...s, liked_by_me: next, likes: s.likes + (next ? 1 : -1) }));
    try {
      const { data } = await api.post(`/kb/articles/${articleId}/like`, { like: next });
      setState((s) => ({ ...s, liked_by_me: data.liked_by_me, likes: data.likes }));
    } catch (e) {
      setState((s) => ({ ...s, liked_by_me: !next, likes: s.likes + (next ? -1 : 1) }));
      toast.error(apiError(e));
    }
  };

  const addComment = async (content) => {
    const { data } = await api.post(`/kb/articles/${articleId}/comments`, { content });
    setComments((c) => [data, ...(c || [])]);
    setState((s) => ({ ...s, comments: s.comments + 1 }));
    return data;
  };

  const deleteComment = async (id) => {
    setConfirmDelete(null);
    const prev = comments;
    setComments((c) => c.filter((x) => x.id !== id));
    setState((s) => ({ ...s, comments: Math.max(0, s.comments - 1) }));
    try {
      await api.delete(`/kb/articles/${articleId}/comments/${id}`);
    } catch (err) {
      setComments(prev);
      setState((s) => ({ ...s, comments: s.comments + 1 }));
      toast.error(apiError(err));
    }
  };

  const scrollToComments = () => {
    document.getElementById(COMMENTS_ANCHOR_ID)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const value = useMemo(
    () => ({
      user,
      state,
      comments,
      composerOpen,
      setComposerOpen,
      confirmDelete,
      setConfirmDelete,
      toggleFavorite,
      toggleLike,
      addComment,
      deleteComment,
      scrollToComments,
    }),
    [user, state, comments, composerOpen, confirmDelete],
  );

  return (
    <EngagementCtx.Provider value={value}>
      {children}
      <CommentComposerModal />
      <DeleteCommentDialog />
    </EngagementCtx.Provider>
  );
}

/** Barra de acciones horizontal, pensada para ir sticky bajo el header. */
export function KBEngagementBar({ className = '' }) {
  const ctx = useEngagement();
  if (!ctx) return null;
  const { state, toggleFavorite, toggleLike, setComposerOpen, scrollToComments } = ctx;

  return (
    <div
      className={`sticky top-16 z-30 -mx-4 border-b border-border/60 bg-background/85 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/70 sm:-mx-6 sm:px-6 ${className}`}>
      <div className="mx-auto flex max-w-5xl items-center gap-1 py-2">
        <ActionButton
          icon={ThumbsUpIcon}
          label="Me gusta"
          count={state.likes}
          active={state.liked_by_me}
          activeColor="text-secondary"
          onClick={toggleLike}
        />
        <ActionButton
          icon={Bookmark01Icon}
          label="Guardar"
          count={state.favorites}
          active={state.favorited_by_me}
          activeColor="text-primary"
          onClick={toggleFavorite}
        />
        <ActionButton icon={Comment01Icon} label="Comentar" onClick={() => setComposerOpen(true)} />
        <button
          type="button"
          onClick={scrollToComments}
          className="ml-auto rounded-md px-2 py-1 text-sm text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
          {state.comments > 0
            ? `Ver ${state.comments} comentario${state.comments === 1 ? '' : 's'}`
            : 'Ver comentarios'}
        </button>
      </div>
    </div>
  );
}

function ActionButton({ icon, label, count, active, activeColor = 'text-foreground', onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active === undefined ? undefined : !!active}
      className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ${
        active ? activeColor : 'text-muted-foreground'
      }`}>
      <HugeiconsIcon icon={icon} className={`h-[18px] w-[18px] ${active ? 'fill-current' : ''}`} aria-hidden="true" />
      <span>{label}</span>
      {typeof count === 'number' && count > 0 && <span className="tabular-nums">{count}</span>}
    </button>
  );
}

/** Hilo de comentarios, pensado para ir al final de la página. */
export function KBComments({ className = '' }) {
  const ctx = useEngagement();
  if (!ctx) return null;
  const { state, comments, setComposerOpen } = ctx;

  return (
    <section id={COMMENTS_ANCHOR_ID} aria-labelledby="kb-comentarios-title" className={`scroll-mt-28 ${className}`}>
      <div className="flex items-center justify-between gap-3">
        <h2 id="kb-comentarios-title" className="font-display text-2xl font-extrabold text-foreground">
          Comentarios{state.comments > 0 ? ` (${state.comments})` : ''}
        </h2>
        <Button size="sm" className="gap-2" onClick={() => setComposerOpen(true)}>
          <HugeiconsIcon icon={Message01Icon} className="h-4 w-4" aria-hidden="true" /> Comentar
        </Button>
      </div>

      <ul className="mt-6 space-y-5">
        {comments === null && <li className="text-sm text-muted-foreground">Cargando comentarios…</li>}
        {comments !== null && comments.length === 0 && (
          <li className="rounded-2xl border border-dashed border-border py-10 text-center text-sm text-muted-foreground">
            Aún no hay comentarios. Sé la primera persona en comentar.
          </li>
        )}
        {comments?.map((c) => (
          <CommentItem key={c.id} comment={c} />
        ))}
      </ul>
    </section>
  );
}

function CommentItem({ comment }) {
  const { user, setConfirmDelete } = useEngagement();
  const canDelete = user && (comment.author_id === user.id || user.role === 'admin');
  const initials = (comment.author || '?')
    .split(' ')
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();
  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return '';
    }
  };
  return (
    <li className="flex gap-3">
      <span
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary"
        aria-hidden="true">
        {initials}
      </span>
      <div className="flex-1 rounded-2xl bg-muted/40 px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-foreground">{comment.author}</span>
          <span className="text-xs text-muted-foreground">{fmtDate(comment.created_at)}</span>
          {canDelete && (
            <button
              type="button"
              onClick={() => setConfirmDelete(comment)}
              className="ml-auto inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-muted-foreground transition-colors hover:text-destructive focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              aria-label={`Eliminar comentario de ${comment.author}`}>
              <HugeiconsIcon icon={Delete02Icon} className="h-3.5 w-3.5" aria-hidden="true" /> Eliminar
            </button>
          )}
        </div>
        <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">{comment.content}</p>
      </div>
    </li>
  );
}

function CommentComposerModal() {
  const { user, composerOpen, setComposerOpen, addComment } = useEngagement();
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);

  const close = () => {
    setComposerOpen(false);
    setDraft('');
  };

  const submit = async (e) => {
    e.preventDefault();
    const content = draft.trim();
    if (!content || busy) return;
    setBusy(true);
    try {
      await addComment(content);
      toast.success('Comentario publicado');
      close();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={composerOpen} onOpenChange={(o) => (o ? setComposerOpen(true) : close())}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Escribir un comentario</DialogTitle>
          <DialogDescription>
            {user ? `Comentando como ${user.name}` : 'Comparte tu opinión sobre este contenido.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit}>
          <label htmlFor="kb-comment-composer" className="sr-only">
            Comentario
          </label>
          <Textarea
            id="kb-comment-composer"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="¿Te resultó útil? Comparte tu opinión o una duda…"
            maxLength={2000}
            rows={5}
            autoFocus
            className="resize-y"
          />
          <div className="mt-1 text-right text-xs text-muted-foreground">{draft.length}/2000</div>
          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={close}>
              Cancelar
            </Button>
            <Button type="submit" className="gap-2" disabled={!draft.trim() || busy}>
              <HugeiconsIcon icon={SentIcon} className="h-4 w-4" aria-hidden="true" />
              {busy ? 'Publicando…' : 'Publicar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeleteCommentDialog() {
  const { confirmDelete, setConfirmDelete, deleteComment } = useEngagement();
  return (
    <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Eliminar comentario</AlertDialogTitle>
          <AlertDialogDescription>
            ¿Seguro que quieres eliminar tu comentario? Esta acción no se puede deshacer.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={() => deleteComment(confirmDelete?.id)}>
            Eliminar
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
